import type { RawRow, SiteAggregate } from "../context/DataContext";

export interface AlertConfig {
  wanWarnSigma: number;
  wanCritSigma: number;
  unauthWarnPct: number;
  unauthCritPct: number;
  staticWarnPct: number;
  staticCritPct: number;
  authPerUserGB: number;
  consecWarnDays: number;
  consecCritDays: number;
}

export const DEFAULT_ALERT_CONFIG: AlertConfig = {
  wanWarnSigma: 1.5,
  wanCritSigma: 2.0,
  unauthWarnPct: 10,
  unauthCritPct: 15,
  staticWarnPct: 15,
  staticCritPct: 25,
  authPerUserGB: 1.0,
  consecWarnDays: 3,
  consecCritDays: 5,
};

export interface AlertReason {
  rule: string;
  detail: string;
  level: "critical" | "warning";
}

export interface VesselAlert {
  site: string;
  level: "critical" | "warning";
  reasons: AlertReason[];
}

function maxConsecutive(values: number[], threshold: number): number {
  let max = 0, cur = 0;
  for (const v of values) {
    if (v > threshold) { cur++; max = Math.max(max, cur); }
    else cur = 0;
  }
  return max;
}

export function computeAlerts(
  rawData: RawRow[],
  siteAggregates: SiteAggregate[],
  cfg: AlertConfig
): VesselAlert[] {
  const wanValues = siteAggregates.map(s => s.totalWan);
  const mean = wanValues.reduce((a, b) => a + b, 0) / (wanValues.length || 1);
  const std = Math.sqrt(
    wanValues.map(v => (v - mean) ** 2).reduce((a, b) => a + b, 0) / (wanValues.length || 1)
  );

  const result: VesselAlert[] = [];

  for (const agg of siteAggregates) {
    const vesselRows = rawData
      .filter(r => r.site === agg.site)
      .sort((a, b) => a.date.localeCompare(b.date));

    const reasons: AlertReason[] = [];
    const sigmas = std > 0 ? (agg.totalWan - mean) / std : 0;

    if (sigmas > cfg.wanCritSigma) {
      reasons.push({
        rule: "High Fleet WAN",
        detail: `${agg.totalWan.toFixed(1)} GB — ${sigmas.toFixed(1)}σ above mean (${mean.toFixed(1)} ± ${std.toFixed(1)} GB)`,
        level: "critical",
      });
    } else if (sigmas > cfg.wanWarnSigma) {
      reasons.push({
        rule: "High Fleet WAN",
        detail: `${agg.totalWan.toFixed(1)} GB — ${sigmas.toFixed(1)}σ above mean (${mean.toFixed(1)} ± ${std.toFixed(1)} GB)`,
        level: "warning",
      });
    }

    const worstUnauth = vesselRows.reduce<{ day: string; pct: number } | null>((w, r) => {
      const pct = r.wan > 0 ? (r.unauth / r.wan) * 100 : 0;
      return (!w || pct > w.pct) ? { day: r.label, pct } : w;
    }, null);
    if (worstUnauth) {
      if (worstUnauth.pct > cfg.unauthCritPct) {
        reasons.push({ rule: "High Unauthorized Traffic", detail: `Peak ${worstUnauth.pct.toFixed(1)}% on ${worstUnauth.day} (threshold: ${cfg.unauthCritPct}%)`, level: "critical" });
      } else if (worstUnauth.pct > cfg.unauthWarnPct) {
        reasons.push({ rule: "Elevated Unauthorized Traffic", detail: `Peak ${worstUnauth.pct.toFixed(1)}% on ${worstUnauth.day} (threshold: ${cfg.unauthWarnPct}%)`, level: "warning" });
      }
    }

    const worstStatic = vesselRows.reduce<{ day: string; pct: number } | null>((w, r) => {
      const pct = r.wan > 0 ? (r.staticDevices / r.wan) * 100 : 0;
      return (!w || pct > w.pct) ? { day: r.label, pct } : w;
    }, null);
    if (worstStatic) {
      if (worstStatic.pct > cfg.staticCritPct) {
        reasons.push({ rule: "High Static Device Usage", detail: `Peak ${worstStatic.pct.toFixed(1)}% on ${worstStatic.day} (threshold: ${cfg.staticCritPct}%)`, level: "critical" });
      } else if (worstStatic.pct > cfg.staticWarnPct) {
        reasons.push({ rule: "Elevated Static Device Usage", detail: `Peak ${worstStatic.pct.toFixed(1)}% on ${worstStatic.day} (threshold: ${cfg.staticWarnPct}%)`, level: "warning" });
      }
    }

    const maxConsec = maxConsecutive(vesselRows.map(r => r.authPerUser), cfg.authPerUserGB);
    if (maxConsec >= cfg.consecCritDays) {
      reasons.push({ rule: "Sustained High Auth/User", detail: `${maxConsec} consecutive days > ${cfg.authPerUserGB} GB/user (critical: ${cfg.consecCritDays} days)`, level: "critical" });
    } else if (maxConsec >= cfg.consecWarnDays) {
      reasons.push({ rule: "Elevated Auth/User Streak", detail: `${maxConsec} consecutive days > ${cfg.authPerUserGB} GB/user (warning: ${cfg.consecWarnDays} days)`, level: "warning" });
    }

    if (reasons.length === 0) continue;
    result.push({
      site: agg.site,
      level: reasons.some(r => r.level === "critical") ? "critical" : "warning",
      reasons,
    });
  }

  return result.sort((a, b) => {
    if (a.level !== b.level) return a.level === "critical" ? -1 : 1;
    return a.site.localeCompare(b.site);
  });
}
